import { useState, useCallback, useRef } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TimelineIcon from '@mui/icons-material/Timeline';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import type { OHLCVPoint } from '../../api/types';

// ── Public interface ────────────────────────────────────────────────
// Both ConvergenceChart and PriceChart map their data into this shape
// before passing it to ExportButton. No chart-specific logic lives here.

export interface ExportSeries {
    label: string;
    data: OHLCVPoint[];
    /** When true the "open" value represents a price at that timestamp.
     *  When false (default) it's a probability / generic value. */
    isPrice?: boolean;
}

export interface ExportButtonProps {
    series: ExportSeries[];
    filenamePrefix?: string;
    disabled?: boolean;
}

// ── Helpers ─────────────────────────────────────────────────────────

function sanitize(s: string): string {
    return s
        .replace(/[^a-zA-Z0-9 _-]/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .toLowerCase();
}

function fmtTs(d: Date): string {
    return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z');
}

function buildTimestampIndex(series: ExportSeries[]): Date[] {
    const seen = new Map<number, Date>();
    for (const s of series) {
        for (const p of s.data) {
            const ms = p.t.getTime();
            if (!seen.has(ms)) seen.set(ms, p.t);
        }
    }
    return [...seen.values()].sort((a, b) => a.getTime() - b.getTime());
}

type PointMap = Map<number, OHLCVPoint>;

function indexByTime(data: OHLCVPoint[]): PointMap {
    const m = new Map<number, OHLCVPoint>();
    for (const p of data) m.set(p.t.getTime(), p);
    return m;
}

function escapeCsv(v: string): string {
    if (v.includes(',') || v.includes('"') || v.includes('\n')) {
        return `"${v.replace(/"/g, '""')}"`;
    }
    return v;
}

// ── CSV builders ────────────────────────────────────────────────────

function buildSeriesCsv(series: ExportSeries[], timestamps: Date[]): string {
    const indexes = series.map(s => indexByTime(s.data));
    const header = ['timestamp', ...series.map(s => sanitize(s.label))];
    const rows = [header.join(',')];

    for (const t of timestamps) {
        const ms = t.getTime();
        const cols: string[] = [fmtTs(t)];
        for (let i = 0; i < series.length; i++) {
            const pt = indexes[i].get(ms);
            cols.push(pt != null ? String(pt.open) : '');
        }
        rows.push(cols.join(','));
    }
    return rows.join('\n');
}

function buildCandleCsv(series: ExportSeries[], timestamps: Date[]): string {
    const indexes = series.map(s => indexByTime(s.data));
    const header = ['timestamp'];
    for (const s of series) {
        const l = sanitize(s.label);
        header.push(`${l}_open`, `${l}_high`, `${l}_low`, `${l}_close`, `${l}_volume`);
    }
    const rows = [header.join(',')];

    for (const t of timestamps) {
        const ms = t.getTime();
        const cols: string[] = [fmtTs(t)];
        for (let i = 0; i < series.length; i++) {
            const pt = indexes[i].get(ms);
            if (pt != null) {
                cols.push(
                    String(pt.open),
                    String(pt.high),
                    String(pt.low),
                    String(pt.close),
                    String(pt.vol),
                );
            } else {
                cols.push('', '', '', '', '');
            }
        }
        rows.push(cols.join(','));
    }
    return rows.join('\n');
}

// ── Download trigger ────────────────────────────────────────────────

function downloadCsv(csv: string, filename: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// ── Component ───────────────────────────────────────────────────────

export function ExportButton({ series, filenamePrefix = 'export', disabled }: ExportButtonProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const btnRef = useRef<HTMLButtonElement>(null);

    const hasData = series.length > 0 && series.some(s => s.data.length > 0);

    const handleExport = useCallback(
        (mode: 'series' | 'candle') => {
            setAnchorEl(null);
            if (!hasData) return;

            const timestamps = buildTimestampIndex(series);
            const dateSuffix = new Date().toISOString().slice(0, 10);
            const prefix = sanitize(filenamePrefix) || 'export';

            const csv =
                mode === 'series'
                    ? buildSeriesCsv(series, timestamps)
                    : buildCandleCsv(series, timestamps);

            downloadCsv(csv, `${prefix}_${mode}_${dateSuffix}.csv`);
        },
        [series, filenamePrefix, hasData],
    );

    return (
        <>
            <Tooltip title="Export data">
                <span>
                    <IconButton
                        ref={btnRef}
                        size="small"
                        disabled={disabled || !hasData}
                        onClick={e => setAnchorEl(e.currentTarget)}
                        sx={{ p: 0.5 }}
                    >
                        <DownloadIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </span>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: { sx: { minWidth: 180 } },
                }}
            >
                <MenuItem dense onClick={() => handleExport('series')}>
                    <ListItemIcon>
                        <TimelineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Series CSV"
                        secondary="One value per timestamp"
                        primaryTypographyProps={{ sx: { fontSize: '13px' } }}
                        secondaryTypographyProps={{ sx: { fontSize: '11px' } }}
                    />
                </MenuItem>
                <MenuItem dense onClick={() => handleExport('candle')}>
                    <ListItemIcon>
                        <CandlestickChartIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Candle CSV"
                        secondary="OHLCV per timestamp"
                        primaryTypographyProps={{ sx: { fontSize: '13px' } }}
                        secondaryTypographyProps={{ sx: { fontSize: '11px' } }}
                    />
                </MenuItem>
            </Menu>
        </>
    );
}